import PostHog from 'posthog-react-native';
import config from "./config"
import { Platform } from "react-native"
import { AppEventsLogger, Mixpanel, analytics, FirebaseAnalyticsTypes, UXCam } from '../components';
import _ from "lodash"

class JalaAnalytic {
  public id = '';
  public keys = { mixpanel: null, postHog: null, uxCam: null };

  public mixpanel: Mixpanel;
  public postHog: PostHog;
  public appEventsLogger: typeof AppEventsLogger;
  public analytics: FirebaseAnalyticsTypes.Module;

  public setKey(keys: any) {
    const { MIXPANEL_TOKEN, POSTHOG_API_KEY, UXCAM_APP_KEY } = config();
    const key = {
      mixpanel: MIXPANEL_TOKEN,
      postHog: POSTHOG_API_KEY,
      uxCam: UXCAM_APP_KEY
    };
    this.keys = { ...key, ...keys }

    return this
  }

  public init(keys?: any) {
    if (keys) this.setKey(keys)

    if (this.keys.mixpanel) {
      this.mixpanel = new Mixpanel(this.keys.mixpanel, true);
      this.mixpanel.init().catch()
    }
    if (this.keys.postHog) {
      this.postHog = new PostHog(this.keys.postHog, {
        // PostHog API host (https://app.posthog.com by default)
        host: 'https://app.posthog.com',
        persistence: Platform.OS === 'web' ? 'memory' : 'file',
      })
    }
    if (AppEventsLogger.logEvent) {
      this.appEventsLogger = AppEventsLogger;
    }
    if (analytics instanceof Function && analytics().logEvent) {
      this.analytics = analytics();
    }

    return this
  }

  public setUserId(value: number|string, properties?: any) {
    this.id = `${value}`;
    if (this.keys.mixpanel && value) {
      this.mixpanel.identify(this.id);
      if (properties)
        this.mixpanel.getPeople().set(properties)
    }
    if (this.keys.postHog && value) {
      this.postHog.identify(this.id, properties);
    }
    if (this.keys.uxCam) {
      UXCam.setUserIdentity(this.id)
      if (properties) {
        UXCam.setUserProperty("name", properties.name)
      }
    }
    if (this.appEventsLogger && value) {
      this.appEventsLogger.setUserID(this.id);
      if (properties)
        this.appEventsLogger.setUserData({
          firstName: properties.name,
          email: properties.email,
          phone: properties.phone
        });
    }
    if (this.analytics && value) {
      this.analytics.setUserId(this.id)
        .then(() => properties && this.analytics.setUserProperties({
          name: properties.name,
          email: properties.email,
          phone: properties.phone
        }));
    }
    return this
  }

  public setUser(value: any) {
    if (this.keys.mixpanel) {
      this.mixpanel.getPeople().set(value);
    }
    if (this.keys.postHog) {
      this.postHog.identify(this.id, value);
    }
    if (this.appEventsLogger) {
      this.appEventsLogger.setUserData({
        firstName: value.name,
        email: value.email,
        phone: value.phone
      });
    }
    if (this.analytics) {
      this.analytics.setUserProperties({
        name: value.name,
        email: value.email,
        phone: value.phone
      }).catch();
    }

    return this
  }

  public log(value: string, properties: any = undefined) {
    if (!value) return this;
    try {
      if (this.keys.mixpanel) {
        this.mixpanel.track(value, properties || {})
      }
      if (this.keys.postHog) {
        this.postHog.capture(value, properties)
      }
      if (this.appEventsLogger) {
        this.appEventsLogger.logEvent(_.upperFirst(_.camelCase(_.snakeCase(value))), properties);
      }
      if (this.analytics) {
        this.analytics.logEvent(_.snakeCase(value).substr(0, 40), properties).catch();
      }
    } catch (e) {
      console.warn(e);
      return this;
    }

    return this
  };

  public timeEvent(value: string) {
    if (this.keys.mixpanel) {
      this.mixpanel.timeEvent(value);
    }

    return this
  }

  public register(properties: { method?: string, currency?: string } = {}) {
    if (this.appEventsLogger) {
      this.appEventsLogger.logEvent(AppEventsLogger.AppEvents.CompletedRegistration, {
        [AppEventsLogger.AppEventParams.RegistrationMethod]: properties.method || 'email',
        [AppEventsLogger.AppEventParams.Currency]: properties.currency || 'IDR'
      });
    }
    if (this.analytics) {
      this.analytics.logSignUp({
        method: properties.method || 'email'
      }).catch();
    }
  }

  public login(properties: { method?: string } = {}) {
    if (this.analytics) {
      this.analytics.logLogin({
        method: properties.method || 'email'
      }).catch();
    }
  }

  public logout() {
    this.id = '';

    if (this.keys.mixpanel) {
      this.mixpanel.reset();
    }
    if (this.keys.postHog) {
      this.postHog.reset();
    }
    if (this.appEventsLogger) {
      this.appEventsLogger.setUserID(null);
    }
    if (this.analytics) {
      this.analytics.setUserId(null).catch();
    }

    return this
  };

  public screen(name: string) {
    if (!name) return this;
    try {
      if (this.keys.mixpanel) {
        this.mixpanel.track('Screen', { name })
      }
      // if (this.keys.postHog) {
      //   this.postHog.screen(name)
      // }
      if (this.appEventsLogger) {
        this.appEventsLogger.logEvent('Screen', { name });
      }
      if (this.keys.uxCam) {
        UXCam.tagScreenName(name)
      }
      if (this.analytics) {
        this.analytics.logScreenView({
          screen_name: name,
          screen_class: name
        }).catch()
      }
    } catch (e) {
      console.warn(e);
    }

    return this;
  }
}

const JalaAnalyticInstance = new JalaAnalytic();

export default JalaAnalyticInstance;
